import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Users, Gift, Mail, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const SESSION_KEY = "zh_referral_popup_dismissed";
const LANG_KEY = "langPref";

export const ZhReferralPopup = ({ delay = 35000 }: { delay?: number }) => {
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const lang = localStorage.getItem(LANG_KEY);
    if (lang !== "zh") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const timer = setTimeout(() => setVisible(true), delay);
    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 5 && !sessionStorage.getItem(SESSION_KEY)) {
        clearTimeout(timer);
        setVisible(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseOut);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", handleMouseOut); };
  }, [delay]);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => { setVisible(false); setClosing(false); sessionStorage.setItem(SESSION_KEY, "1"); }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast({ title: "邮箱格式有误", description: "请输入有效的电子邮件地址。", variant: "destructive" });
      return;
    }

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: { "Content-Type": "application/json", "version": "2021-07-28" },
      body: JSON.stringify({
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "ZH Referral Popup",
        formData: { email },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
        locationId: "M7JmlFFXdtFNLwyfY61D"
      })
    }).catch(() => {});

    setSubmitted(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleCopyLink = async () => {
    const link = `https://uplevelcleaning.com/zh/referral?ref=${btoa(email).substring(0, 8)}`;
    await navigator.clipboard.writeText(link);
    toast({ title: "链接已复制！", description: "发送给您的朋友即可享受优惠。" });
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={cn(
        "relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl transition-all duration-300",
        closing ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"
      )}>
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/60 to-primary bg-[length:200%_auto]" />
        <div className="bg-card p-8">
          <button onClick={dismiss} className="absolute top-4 right-4 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="关闭">
            <X className="h-4 w-4" />
          </button>

          {!submitted ? (
            <>
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-10 w-10 text-primary" />
                  </div>
                  <div className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-green-500 flex items-center justify-center">
                    <Gift className="h-4 w-4 text-white" />
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-extrabold text-center mb-2 leading-tight">
                每推荐一位好友，<br /><span className="text-primary">即获$20奖励！</span>
              </h2>
              <p className="text-muted-foreground text-center text-sm mb-6">
                输入您的邮箱，获取专属推荐链接。您和朋友都将获得$20折扣！
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" placeholder="您的邮箱地址" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-12 rounded-xl" required />
                </div>
                <Button type="submit" size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                  获取推荐链接
                </Button>
              </form>
              <p className="text-center text-xs text-muted-foreground mt-4">🔒 绝不发送垃圾邮件，仅用于发送您的推荐链接。</p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-5">
                <div className="h-20 w-20 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <h2 className="text-2xl font-extrabold mb-2">成功！ 🎉</h2>
              <p className="text-muted-foreground text-sm mb-6">
                您的专属推荐链接已生成，快分享给朋友吧！
              </p>
              <button onClick={handleCopyLink} className="w-full py-4 rounded-2xl border-2 border-dashed border-primary bg-primary/5 text-primary hover:bg-primary/10 transition-all duration-200 font-mono font-bold text-lg">
                复制推荐链接
              </button>
              <p className="text-xs text-muted-foreground mt-2 mb-6">点击复制</p>
              <Button onClick={dismiss} size="lg" className="w-full h-12 font-bold bg-muted text-foreground hover:bg-muted/90">
                关闭
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
